import { LoadingSmall } from "@/components/LoadingSmall";
import { Button } from "@/components/ui/button";
import { showSuccess, showApiError } from "@/lib/toast";
import { ContentGroup } from "@/lib/types";
import { getContentGroupDetails, updateContentGroup } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ShuffleIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

interface Props {
  contentGroup: ContentGroup;
}

export const ContentGroupShuffleButton: React.FC<Props> = ({ contentGroup }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const isShuffleEnabled = contentGroup.isShuffle;

  const queryClient = useQueryClient();
  const shuffleToggleMutation = useMutation({
    mutationFn: async () => {
      const contentGroupExistingLastStateFromDB = await getContentGroupDetails(
        String(activeFirm?.firmGuid),
        contentGroup.contentGroupGuid
      );
      const contentGroupExistingLastStateFromDBContentGroup = contentGroupExistingLastStateFromDB.data as ContentGroup;

      return updateContentGroup({
        firmGuid: String(activeFirm?.firmGuid),
        contentGroupGuid: contentGroup.contentGroupGuid,
        name: contentGroupExistingLastStateFromDBContentGroup.name,
        isShuffle: !contentGroupExistingLastStateFromDBContentGroup.isShuffle,
        screenSizeId: contentGroupExistingLastStateFromDBContentGroup.screenSizeintId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["contentGroupDetails"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  return (
    <Button
      variant="outline"
      onClick={() => shuffleToggleMutation.mutate()}
      disabled={shuffleToggleMutation.isPending}
      size="sm"
    >
      <ShuffleIcon className="w-2 h-2" />
      {isShuffleEnabled ? t("contentGroup.shuffleEnabled") : t("contentGroup.shuffleDisabled")}
      {shuffleToggleMutation.isPending && <LoadingSmall />}
    </Button>
  );
};
